import * as React from 'react';
import { ReactNode } from 'react';
import { FilterProps, FilterType } from '@tanstack/react-table';
import { EllipsisVerticalIcon } from 'lucide-react';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { SortDirection } from '@sib/types';
import { TABLE_DATA_ROW_CELL_CLASSES } from '../constants';
import { Sort, ChevronDown, Info } from '../icons';
import { cn, parseFilterDate, toBackendDayRange } from '../utils';
import { DateFilter } from './DateFilter';
import { DebouncedInput } from './DebouncedInput';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from './DropdownMenu';
import { NumberRangeFilter } from './NumberRangeFilter';
import { SelectFilter } from './SelectFilter';
import { TableTooltip } from './TableTooltip';
import { Tooltip } from './Tooltip';

const TableContainer = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('shadow-subtle relative w-full rounded-md border', className)}
    {...props}
  />
));
TableContainer.displayName = 'TableContainer';

const DataTableContainer = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'relative flex h-full max-h-[calc(100vh-82px)] w-full flex-1 flex-col overflow-hidden',
      className
    )}
    {...props}
  />
));
DataTableContainer.displayName = 'DataTableContainer';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    disableWrapper?: boolean;
    wrapperProps?: React.HTMLAttributes<HTMLDivElement> &
      React.RefAttributes<HTMLDivElement>;
  }
>(({ className, disableWrapper, wrapperProps, ...props }, ref) =>
  disableWrapper ? (
    <table
      ref={ref}
      className={cn('w-full caption-bottom border-separate text-sm', className)}
      cellPadding={0}
      cellSpacing={0}
      {...props}
    />
  ) : (
    <div
      {...wrapperProps}
      className={cn('relative w-full overflow-auto', wrapperProps?.className)}
    >
      <table
        ref={ref}
        className={cn(
          'w-full caption-bottom border-separate text-sm',
          className
        )}
        cellPadding={0}
        cellSpacing={0}
        {...props}
      />
    </div>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'bg-muted/50 border-t font-medium last:[&>tr]:border-b-0',
      '[&>tr]:bg-primary hover:[&>tr]:bg-primary [&>tr]:h-10 [&>tr>td]:h-10 [&>tr>td]:py-2 [&>tr>td]:font-semibold [&>tr>td]:text-white [&>tr>td:first-of-type]:rounded-bl-md [&>tr>td:last-of-type]:rounded-br-md',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { disabled?: boolean }
>(({ className, disabled, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'data-[state=selected]:bg-muted hover:bg-surface-soft border-0 transition-colors',
      '[&:last-of-type:not(:first-of-type)] group even:bg-surface-subtle bg-white',
      disabled && 'data-table-row-disabled',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

export interface TableFilter {
  type: FilterType;
  value: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

export interface TableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  canSort?: boolean;
  sortDirection?: SortDirection;
  size?: number;
  filter?: TableFilter;
  clearFilter?: () => void;
  setFilter?: (values: string[]) => void;
  settingsRow?: boolean;
  filterProps?: FilterProps;
  tooltip?: ReactNode;
  options?: { label: string; onClick: () => void }[];
  withFilters?: boolean;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  (
    {
      canSort = false,
      className,
      sortDirection,
      size,
      children,
      filter,
      clearFilter,
      setFilter,
      settingsRow,
      filterProps,
      tooltip,
      options,
      withFilters = true,
      ...props
    },
    ref
  ) => (
    <th
      ref={ref}
      className={cn(
        'text-muted-foreground h-12 px-4 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
        'bg-primary border-border h-10 border-0 p-0 font-normal text-white first:rounded-tl-md last:rounded-tr-md',
        {
          'cursor-pointer select-none': canSort,
        },
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'bg-primary box-border flex w-full max-w-full min-w-0 flex-col gap-y-1 p-3',
          {
            'pb-0': settingsRow,
          }
        )}
      >
        <div className="flex max-w-full min-w-0 items-start justify-between gap-x-1.5">
          <div className="flex min-w-0 flex-1 items-center gap-x-1.5">
            {tooltip ? (
              <TableTooltip tooltip={tooltip}>
                <span>{children}</span>
              </TableTooltip>
            ) : (
              children
            )}
            {canSort && (
              <span className="inline-block">
                {sortDirection ? (
                  <ChevronDown
                    className={cn('transition-transform', {
                      'rotate-180': sortDirection === 'asc',
                    })}
                  />
                ) : (
                  <Sort className="opacity-30" />
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-x-1.5">
            {tooltip && (
              <TableTooltip tooltip={tooltip}>
                <span className="ml-auto">
                  <Info className="size-3.5" />
                </span>
              </TableTooltip>
            )}
            {(options || []).length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger className="cursor-pointer p-0.5">
                  <EllipsisVerticalIcon className="size-4 text-white" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="bottom"
                  align="end"
                  onClick={e => e.stopPropagation()}
                >
                  {options?.map((option, index) => (
                    <DropdownMenuItem
                      key={`header-option-${index}`}
                      onSelect={option.onClick}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {filter && withFilters && (
          <TableFilters
            filter={filter}
            setFilter={setFilter}
            clearFilter={clearFilter}
            filterProps={filterProps}
            size={size}
          />
        )}
      </div>
    </th>
  )
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'border-0 align-middle [&:has([role=checkbox])]:pr-0',
      'text-muted-foreground border-b-border group-[&:last-of-type:not(:first-of-type)]:border-b',
      TABLE_DATA_ROW_CELL_CLASSES,
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export interface TableBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  colors?: string[];
}

const TableBadge = ({
  children,
  className,
  colors,
  ...props
}: TableBadgeProps) => (
  <span className={cn('chip-badge', className)} {...props}>
    {colors?.length ? (
      <div className="absolute top-0 left-0 h-full w-[5px]">
        {colors.map((color, index) => (
          <div
            key={index}
            className={cn(
              'h-full opacity-100 transition-opacity duration-300',
              {
                'rounded-t-md': index === 0,
                'rounded-b-md': index === colors.length - 1,
              }
            )}
            style={{
              backgroundColor: color,
              height: `${100 / colors.length}%`,
            }}
          />
        ))}
      </div>
    ) : null}

    {children}
  </span>
);

TableBadge.displayName = 'TableBadge';

interface TableFiltersProps {
  filter: TableFilter;
  setFilter?: (values: string[]) => void;
  clearFilter?: () => void;
  filterProps?: FilterProps;
  disabledTooltip?: string;
  size?: number;
}

const FilterComponent = ({
  filter,
  setFilter,
  clearFilter,
  filterProps,
  size,
}: Omit<TableFiltersProps, 'disabledTooltip'>) => {
  const { t } = useTranslation();
  const isEmptyFilter =
    filter.value.length === 1 && filter.value[0] === EMPTY_FILTER_VALUE;
  const emptyValuesPlaceholder = t(
    'components.dataTable.emptyFilter.optionLabel'
  );

  if (filterProps?.type === 'text' || filterProps?.type === 'number')
    return (
      <DebouncedInput
        autoComplete="disabled"
        onClick={e => e.stopPropagation()}
        type={filter.type}
        disabled={filter.disabled}
        hideNumberSpinButtons
        fullWidth
        InputProps={{
          className: cn(
            'px-2 py-1',
            isEmptyFilter &&
              'placeholder:text-primary placeholder:opacity-100 placeholder:font-semibold'
          ),
          ...(isEmptyFilter && {
            placeholder: emptyValuesPlaceholder,
          }),
        }}
        variant="empty"
        className={cn(
          'h-[30px] rounded-sm border bg-white p-0',
          filterProps.InputProps?.className,
          filter.disabled && 'cursor-not-allowed opacity-60'
        )}
        value={isEmptyFilter ? '' : (filter.value[0] ?? '')}
        onChange={value => {
          if (filter.disabled) return;
          const newValue = value.trimStart();
          if (newValue === '') {
            clearFilter?.();
          } else {
            setFilter?.([newValue]);
          }
        }}
      />
    );

  if (filterProps?.type === 'select' || filterProps?.type === 'ci_select')
    return (
      <SelectFilter
        options={filterProps?.options ?? []}
        value={filter.value}
        disabled={filter.disabled}
        onChange={(vals: string[]) => {
          if (filter.disabled) return;
          if (vals.length === 0) return clearFilter?.();

          setFilter?.(vals);
        }}
        onSearchTermChange={filterProps?.onSearchTermChange}
        sentryRef={filterProps?.sentryRef}
        hasNextPage={filterProps?.hasNextPage}
        filterProps={filterProps}
      />
    );

  if (
    filterProps?.type === 'single_select' ||
    filterProps?.type === 'ci_single_select'
  )
    return (
      <SelectFilter
        options={filterProps?.options ?? []}
        value={filter.value}
        disabled={filter.disabled}
        isSingleSelect
        onChange={(vals: string[]) => {
          if (filter.disabled) return;
          if (vals.length === 0) return clearFilter?.();

          setFilter?.(vals);
        }}
        filterProps={filterProps}
      />
    );

  if (filterProps?.type === 'date') {
    return (
      <DateFilter
        {...(isEmptyFilter
          ? {
              from: null,
              to: null,
            }
          : {
              from: parseFilterDate(filter.value[0]),
              to: parseFilterDate(filter.value[1]),
            })}
        disabled={filter.disabled}
        placeholder={isEmptyFilter ? emptyValuesPlaceholder : undefined}
        onRangeSelect={range => {
          if (!range?.from) return clearFilter?.();
          setFilter?.(toBackendDayRange(range.from, range.to));
        }}
      />
    );
  }

  if (filterProps?.type === 'number_range') {
    return (
      <NumberRangeFilter
        value={filter.value}
        disabled={filter.disabled}
        {...(isEmptyFilter && {
          placeholder: emptyValuesPlaceholder,
        })}
        {...filterProps?.ranges}
        size={size}
        filterProps={filterProps}
        onChange={(operator, from, to) => setFilter?.([operator, from, to])}
        onClear={clearFilter}
      />
    );
  }

  return null;
};

const TableFilters = ({
  filter,
  setFilter,
  clearFilter,
  filterProps,
  size,
}: TableFiltersProps) => {
  const filterComponentProps = React.useMemo(
    () => ({
      filter,
      setFilter,
      clearFilter,
      filterProps,
      size,
    }),
    [filter, setFilter, clearFilter, filterProps, size]
  );

  if (!filter?.disabled || !filterProps?.disabledTooltip)
    return <FilterComponent {...filterComponentProps} />;

  return (
    <Tooltip
      content={filterProps?.disabledTooltip}
      TooltipTriggerProps={{ asChild: true }}
    >
      <span>
        <FilterComponent {...filterComponentProps} />
      </span>
    </Tooltip>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableBadge,
  TableContainer,
  DataTableContainer,
};
